import { useCallback, useMemo } from "react";
import { ResizablePanel } from "@/components/ui/resizable";
import type { NewsFeedFilterCategory } from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import { useFeature } from "@/lib/feature-management";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import useNewsFeedSidebarFilters from "@/store/news-feed/filters/useNewsFeedSidebarFilters";
import { ArticleTagsFilterContainer } from "./article-tags-filter/ArticleTagsFilterContainer";
import KeyMessageFilterContainer from "./key-messages-filter/KeyMessageFilterContainer";
import NewsFeedDynamicFiltersContainer from "./NewsFeedDynamicFiltersContainer";
import NewsFeedStaticFiltersContainer from "./NewsFeedStaticFiltersContainer";
import NarrativeClusterFilterContainer from "./narrative-clusters-filter/NarrativeClusterFilterContainer";
import PublisherMediaListFilterContainer from "./publisher-media-list-filter/PublisherMediaListFilterContainer";

/**
 * Wrapper component that only renders when categories are ready.
 * This prevents Rules of Hooks violations by ensuring the hook is always
 * called with the same number of categories.
 */
const SidebarFiltersLoader = ({
  categories,
  shouldFetchCategory,
}: {
  categories: NewsFeedFilterCategory[];
  shouldFetchCategory: (category: NewsFeedFilterCategory) => boolean;
}) => {
  useNewsFeedSidebarFilters({
    categories,
    shouldFetchCategory,
  });
  return null;
};

const NewsFeedFilterContainer = () => {
  const isDictionaryLoading = useFilterDictionary((state) => state.isLoading);
  const sidebarFilters = useFilterDictionary((state) => state.sidebarFilters);
  const expandedCategories = useNewsFeedFilters(
    (state) => state.expandedCategories,
  );
  const enableArticleTagging = useFeature("enable-article-tagging", false);

  const shouldFetchCategory = useCallback(
    (category: NewsFeedFilterCategory) => expandedCategories.has(category),
    [expandedCategories],
  );

  const dictionaryCategories = useMemo(
    () => sidebarFilters.map((filter) => filter.category),
    [sidebarFilters],
  );

  const hasCategories = !isDictionaryLoading && dictionaryCategories.length > 0;

  return (
    <ResizablePanel defaultSize={100} minSize={30}>
      {/* Only render the loader when categories are ready to prevent hook count changes */}
      {hasCategories && (
        <SidebarFiltersLoader
          categories={dictionaryCategories}
          shouldFetchCategory={shouldFetchCategory}
        />
      )}

      <div className="flex h-full w-full flex-col">
        <div className="overflow-y-auto h-full scrollbar-thin scrollbar-thumb-gray-150">
          <NarrativeClusterFilterContainer />
          <KeyMessageFilterContainer />
          {enableArticleTagging && <ArticleTagsFilterContainer />}
          <PublisherMediaListFilterContainer />
          <NewsFeedStaticFiltersContainer />
          <NewsFeedDynamicFiltersContainer />
        </div>
      </div>
    </ResizablePanel>
  );
};

export default NewsFeedFilterContainer;
